import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import { Subject, Subscription } from 'rxjs';
import { Translatable } from './translatable';
import { TranslatorComponent } from './translator.component';

@Directive({
  selector: 'input[atmTranslatable], textarea[atmTranslatable]',
  providers: [
    { provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: TranslatableDirective },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TranslatableDirective),
      multi: true,
    },
  ],
})
export class TranslatableDirective implements ControlValueAccessor, OnDestroy {
  private closedSubscription = Subscription.EMPTY;

  readonly stateChanges = new Subject<void>();

  @Input()
  defaultLang = this.locale;

  @Input()
  set atmTranslatable(translator: TranslatorComponent | undefined) {
    if (translator) {
      this.translator = translator;
      this.closedSubscription.unsubscribe();
      this.closedSubscription = translator.closed.subscribe((value) => {
        if (value) {
          this.value = value;
          this.onChange(value);
        }
        this.onTouched();
      });
      translator.registerInput(this);
    }
  }
  translator?: TranslatorComponent;

  @Input()
  get disabled(): boolean {
    return this.elementRef.nativeElement.disabled;
  }
  set disabled(isDisabled: boolean) {
    this.elementRef.nativeElement.disabled = isDisabled;
    if (this.translator) {
      this.translator.disabled = isDisabled;
    }
    this.stateChanges.next();
  }

  @Input('value')
  get value(): Translatable | undefined {
    return this.translation;
  }
  set value(value: Translatable | undefined) {
    if (value !== this.translation) {
      this.translation = { ...value };
      this.elementRef.nativeElement.value =
        value && value[this.defaultLang] ? value[this.defaultLang] : '';
    }
  }
  private translation?: Translatable;

  onChange = (_: Translatable) => {};
  onTouched = () => {};

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    @Inject(LOCALE_ID) public readonly locale: string
  ) {}

  ngOnDestroy(): void {
    this.closedSubscription.unsubscribe();
    this.stateChanges.complete();
  }

  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    this.translation = this.translation || {};
    this.translation[this.defaultLang] = value;
    this.onChange(this.translation);
    this.stateChanges.next();
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: Translatable | undefined): void {
    this.value = value;
  }

  registerOnChange(onChange: (_: Translatable) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
